import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams, Navigate } from "react-router-dom";
import DomainHiddenSeoContent from "../../components/agentic/DomainHiddenSeoContent";
import {
  SITE_BASE_URL,
  buildCombinedFaqJsonLd,
  buildGroupBreadcrumbJsonLd,
  buildItemListJsonLd,
} from "../../data/agenticSeoUtils";
import {
  basePathForMode,
  pageModeFromPath,
  resolveUseCaseDomain,
} from "../../data/useCasePageRegistry";

const PAGE_STYLES = `
  .uc-group-page { background: #f5f8fc; min-height: 100vh; }
  .uc-group-main { max-width: 1100px; margin: 0 auto; padding: 32px 24px 64px; }
  .uc-group-breadcrumb ol { display: flex; flex-wrap: wrap; gap: 8px; list-style: none; padding: 0; margin: 0 0 24px; font-size: 14px; color: #5d7493; }
  .uc-group-breadcrumb a { color: #1d3f7d; text-decoration: none; }
  .uc-group-breadcrumb a:hover { text-decoration: underline; }
  .uc-group-title { font-family: Exo, sans-serif; color: #0e2440; font-size: 34px; line-height: 1.2; margin: 0 0 14px; }
  .uc-group-intro { font-size: 16px; line-height: 1.75; color: #2a3a4c; max-width: 820px; margin: 0; }
  .uc-group-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; margin-top: 20px; }
  .uc-group-card { background: #fff; border-radius: 12px; overflow: hidden; border: 1px solid #dbe7f7; box-shadow: 0 4px 16px rgba(29,63,125,0.06); }
  .uc-group-card a { text-decoration: none; color: inherit; display: block; }
  .uc-group-card h3 { font-family: Exo, sans-serif; font-size: 18px; color: #1d3f7d; margin: 0 0 8px; }
  .uc-group-card p { font-size: 14px; line-height: 1.6; color: #4a5a6c; margin: 0; }
  .uc-group-card-body { padding: 16px 18px 20px; }
  .uc-group-card img { width: 100%; height: 160px; object-fit: cover; display: block; }
  .uc-group-related { margin-top: 48px; }
  .uc-group-related h2 { font-family: Exo, sans-serif; font-size: 22px; color: #0e2440; }
  .uc-group-related li { margin-bottom: 8px; }
`;

export default function DomainGroupPage() {
  const { domainSlug, groupId } = useParams();
  const location = useLocation();
  const mode = pageModeFromPath(location.pathname);
  const basePath = basePathForMode(mode);
  const bundle = resolveUseCaseDomain(mode, domainSlug);

  if (!bundle) {
    return <Navigate to={basePath} replace />;
  }

  const group = bundle.groups.find((g) => g.id === groupId);
  const useCases = bundle.useCasesByGroup[groupId] || [];

  if (!group || useCases.length === 0) {
    return <Navigate to={`${basePath}/${domainSlug}`} replace />;
  }

  const canonicalPath = `${basePath}/${domainSlug}/${group.id}`;
  const canonicalUrl = `${SITE_BASE_URL}${canonicalPath}`;
  const automationLabel = mode === "rpa" ? "RPA" : "Agentic AI";
  const metaTitle = `${group.label} ${automationLabel} Use Cases | Surens Inffotek`;
  const metaDescription = `Explore ${useCases.length} ${automationLabel.toLowerCase()} use cases for ${group.label} in ${bundle.intro.title}. Practical automation solutions for enterprises in India, UAE, KSA, UK, and USA.`;
  const itemListJsonLd = buildItemListJsonLd(useCases, `${group.label} Use Cases`);
  const faqJsonLd = buildCombinedFaqJsonLd([], useCases);
  const breadcrumbJsonLd = buildGroupBreadcrumbJsonLd({
    mode,
    domainSlug,
    domainIntro: bundle.intro,
    group,
  });
  const webPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: metaTitle,
    description: metaDescription,
    url: canonicalUrl,
    isPartOf: {
      "@type": "WebSite",
      name: "Surens Inffotek",
      url: SITE_BASE_URL,
    },
    mainEntity: itemListJsonLd,
  };

  return (
    <div className="uc-group-page">
      <Helmet>
        <html lang="en" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="author" content="Surens Inffotek" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/plain" title="LLM summary" href={`${SITE_BASE_URL}/llms.txt`} />
        <link rel="alternate" type="text/plain" title="LLM agentic catalog" href={`${SITE_BASE_URL}/llms-full.txt`} />
        <link rel="alternate" type="text/plain" title="LLM RPA catalog" href={`${SITE_BASE_URL}/llms-rpa.txt`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="Surens Inffotek" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SITE_BASE_URL}/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${SITE_BASE_URL}/logo.png`} />
        <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <style>{PAGE_STYLES}</style>


      <main className="uc-group-main">
        <nav aria-label="Breadcrumb" className="uc-group-breadcrumb">
          <ol>
            <li><Link to="/">Home</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to={basePath}>{mode === "rpa" ? "RPA Use Cases" : "Agentic AI"}</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to={`${basePath}/${domainSlug}`}>{bundle.intro.title}</Link></li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{group.label}</li>
          </ol>
        </nav>

        <header>
          <h1 className="uc-group-title">
            {group.label}: {automationLabel} Use Cases
          </h1>
          <p className="uc-group-intro">
            {group.label} teams use {automationLabel.toLowerCase()} automation to replace manual,
            email- and spreadsheet-driven processes with consistent validation, exception handling,
            and audit evidence. Below are {useCases.length} practical use case
            {useCases.length === 1 ? "" : "s"} for {group.label}.
          </p>
        </header>

        <section aria-labelledby="use-cases-heading" style={{ marginTop: 36 }}>
          <h2 id="use-cases-heading" style={{ fontFamily: "Exo, sans-serif", fontSize: 24, color: "#1d3f7d" }}>
            Use Cases
          </h2>
          <div className="uc-group-grid">
            {useCases.map((uc) => (
              <article className="uc-group-card" key={uc.id}>
                <Link to={`${basePath}/${domainSlug}/${group.id}/${uc.id}`}>
                  {uc.image ? (
                    <img src={uc.image} alt={uc.imageAlt || uc.title} loading="lazy" width="320" height="200" />
                  ) : null}
                  <div className="uc-group-card-body">
                    <h3>{uc.title}</h3>
                    <p>{uc.seoSummary || uc.summary}</p>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </section>

        <nav aria-label="Other categories" className="uc-group-related">
          <h2>Other {bundle.intro.title} Categories</h2>
          <ul>
            {bundle.groups
              .filter((g) => g.id !== group.id)
              .map((g) => (
                <li key={g.id}>
                  <Link to={`${basePath}/${domainSlug}/${g.id}`}>{g.label}</Link>
                </li>
              ))}
          </ul>
        </nav>
      </main>

      <DomainHiddenSeoContent domainTitle={group.label} useCases={useCases} />
    </div>
  );
}

